import React, { useState} from "react";
import {
    Button,
    TextField,
    Box,
    Typography,
    Paper,
    Stack,
    Tabs,
    Tab
} from '@mui/material';
import {useDispatch, useSelector} from "react-redux";
import {loginUser, logoutUser, setIsAuthenticated, setManageInfoId, setPhoneNo} from "./models/AuthSlice";
import {useNavigate} from "react-router-dom";
import Api, {APIs} from "../../Api";
import OTPInput from "./components/OTPInput";
import CommonUtils from "../base/utils/CommonUtils";

interface IProps {
    handleClose: (event?: any) => void; // Accepts an optional event parameter
    paperHeight?: number;
}

interface IUser {
    name: string;
    mobileNo: string;
    isStaff: boolean
}

const authorizedUsers: IUser[] = [
    {name: "Ather Ashraf", mobileNo: '3224785104', isStaff:false},
    {name: "Saqib Malik", mobileNo: '3417272272', isStaff: false},
    {name: "Waleed", mobileNo: '3038299106', isStaff: false},
];

const PhoneLoginForm = ({handleLogin, handleClose}: {
    handleLogin: (mobileNo: string) => void,
    handleClose: () => void
}) => {
    const [mobileNo, setMobileNo] = useState<string>("");


    return (
        <Stack component="form" noValidate autoComplete="off">
            <Typography variant="h6" color="secondary" sx={{flexGrow: 1, pt: 1}}>
                Login with Mobile Number
            </Typography>
            <TextField
                helperText="Sample mobile no. 3224785104"
                type="tel"
                size="small"
                label="Enter Mobile Number"
                variant="standard"
                value={mobileNo}
                onChange={(e) => setMobileNo(e.target.value)}
            />
            <Box sx={{
                display: "flex",
                flexDirection: "row",
                padding: 5,
                alignItems: "center",
                justifyContent: "center"
            }}>
                <Button color="secondary" size="large" variant="contained" onClick={() => handleLogin(mobileNo)}>
                    Send OTP
                </Button>
                <Button color="secondary" size="large" variant="contained" onClick={() => handleClose()}>
                    Close
                </Button>
            </Box>
        </Stack>
    );
}

const UsernamePasswordForm = ({handleLogin, handleClose}: {
    handleLogin: (username: string, password: string) => void,
    handleClose: () => void
}) => {
    const [username, setUsername] = useState<string>("");
    const [password, setPassword] = useState<string>("");

    return (
        <Stack component="form" noValidate autoComplete="off">
            <Typography variant="h6" color="secondary" sx={{flexGrow: 1, pt: 1}}>
                Login with Username and Password
            </Typography>
            <TextField
                size="small"
                label="Username"
                variant="standard"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
            />
            <TextField
                type="password"
                size="small"
                label="Password"
                variant="standard"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
            />
            <Box sx={{
                display: "flex",
                flexDirection: "row",
                padding: 5,
                alignItems: "center",
                justifyContent: "center"
            }}>
                <Button color="secondary" size="large" variant="contained"
                        onClick={() => handleLogin(username, password)}>
                    Login
                </Button>
                <Button color="secondary" size="large" variant="contained" onClick={() => handleClose()}>
                    Close
                </Button>
            </Box>
        </Stack>
    );
}

const OTPLoginForm = ({handleClose, paperHeight = 300}: IProps) => {
    const allowEditing = process.env.REACT_APP_ALLOW_EDITING?.toLowerCase() === 'true';
    const [isOTPSent, setIsOTPSent] = useState(false);
    const [mobileNo, setMobileNo] = useState<string | undefined>();
    // const [isAuthorizedUser, setIsAuthorizedUser] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isAuthenticated = useSelector((state: any) => state.auth.isAuthenticated);
    const isStaffUser = useSelector((state: any) => state.auth.isStaffUser)
    const [tabValue, setTabValue] = useState(0); // 0 for OTP, 1 for Username/Password

    // useEffect(() => {
    //     setTabValue(isStaffUser ? 0 : 1)
    //
    // }, [isStaffUser])

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => setTabValue(newValue);

    const handleMobileLogin = (mobileNo: string) => {
        setMobileNo(mobileNo);
        Api.get(APIs.API_OTP, {phone_no: mobileNo}).then((payload) => {
            if (payload) {
                dispatch(setPhoneNo(mobileNo));
                setIsOTPSent(true);
            } else {
                CommonUtils.showSnackbar("Error sending OTP");
            }
        }).catch(error => {
            console.error("Fetch error:", error);
            CommonUtils.showSnackbar("Error in sending OTP");
        });
    };

    const handleUsernamePasswordLogin = (username: string, password: string) => {

        Api.post(APIs.API_LOGIN, {username, password: CommonUtils.encryptPassword(password)}).then((payload) => {
            // console.log(payload)
            if (payload?.is_authenticated) {
                dispatch(setIsAuthenticated(true));
                dispatch(loginUser(payload));
                // setIsAuthorizedUser(true);
                CommonUtils.showSnackbar("Login Successful");
            } else {
                CommonUtils.showSnackbar("Invalid username or password");
            }
        }).catch(error => {
            console.error("Login error:", error);
            CommonUtils.showSnackbar("Error logging in");
        });
    };

    const verifyOTP = (otp: string) => {
        Api.get(APIs.API_VERIFY_OTP, {phone_no: mobileNo, otp_code: otp}).then((payload) => {
            if (payload?.is_verified) {
                const user = authorizedUsers.find(user => user.mobileNo === mobileNo);
                dispatch(setIsAuthenticated(true));
                dispatch(setManageInfoId({"manageInfoId": payload.manage_info_id, "user": user}));
                setIsOTPSent(false);
                CommonUtils.showSnackbar("OTP Verified");
            } else {
                CommonUtils.showSnackbar("Failed to verify OTP");
            }
        }).catch(error => {
            console.error("Verification error:", error);
            CommonUtils.showSnackbar("Error verifying OTP");
        });
    };

    return (
        <Paper elevation={10} sx={{
            m: 1, p: 2, height: paperHeight, width: 400,
            display: "flex", flexDirection: "column", justifyContent: "center"
        }}>
            {!isAuthenticated ? <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
                <Tab label="OTP Login"/>
                <Tab label="Username & Password"/>
            </Tabs> : <></>}
            {isAuthenticated ? (
                <Stack spacing={2}>
                    <Button color="secondary" onClick={() => navigate("/upload_concept_paper")}>Upload Concept
                        Paper</Button>
                    {(allowEditing && isStaffUser) && <>
                        <Button color="secondary" onClick={() => navigate("/projects_editing")}>Project Information
                            Editing</Button>
                        <Button color="secondary" onClick={() => navigate("/pledge_editing")}>Pledge
                            StatusEditing</Button>
                    </>
                    }
                    <Box sx={{
                        display: "flex",
                        flexDirection: "row",
                        padding: 5,
                        alignItems: "center",
                        justifyContent: "center"
                    }}>
                        <Button color="secondary" size="large" variant="contained"
                                onClick={() => {
                                    dispatch(logoutUser({}))
                                    setTimeout(() => window.location.reload(), 500);
                                }}>
                            Logout
                        </Button>
                        <Button color="secondary" size="large" variant="contained" onClick={() => handleClose()}>
                            Close
                        </Button>
                    </Box>
                </Stack>
            ) : (
                tabValue === 0 ?
                    (!isOTPSent ? <PhoneLoginForm handleLogin={handleMobileLogin} handleClose={handleClose}/> :
                        <OTPInput verifyOTP={verifyOTP}/>)
                    : <UsernamePasswordForm handleLogin={handleUsernamePasswordLogin} handleClose={handleClose}/>
            )}
        </Paper>
    );
}

export default OTPLoginForm;
